import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Typography, Box, color, SelectDropdown, Overlay, Loader } from '@kistech/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { actions as configActions, selectors as configSelectors } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { selectors as clientSelectors } from 'store/clients';
import { getSortedList } from 'utils/stringFormat';
import { ImageUploadOverlay } from '../Shared/ImageUploadOverlay';
import { ImageEditDrawer } from '../Shared/ImageEditDrawer';
import { LinkedContent } from '../Shared/LinkedContent';
import { SlideshowImageList } from '../Shared/Slideshow';
import { VideoMediaList } from '../Shared/VideoMediaList';
import { MediaType, MediaResponse, MediaTypes, ConfigurationMediaProps } from '../../types';

export const AttractLoopMediaDetails: React.FC<ConfigurationMediaProps> = (props: ConfigurationMediaProps) => {
  const { id, configuration } = props.configuration;
  const globalSettings = configuration && configuration.globalSettings;
  const attractLoop = configuration && configuration.globalSettings && configuration.globalSettings.attractLoop;

  const mediaTypeList: MediaType[] = [
    { name: 'Slideshow', value: 'attract-image' },
    { name: 'Video', value: 'attract-video' },
    { name: 'Linked Content', value: 'attract-url' }
  ];
  let defaultMedia = mediaTypeList[0];
  if (attractLoop && attractLoop.type === MediaTypes.ATTRACT_IMAGE) defaultMedia = mediaTypeList[0];
  if (attractLoop && attractLoop.type === MediaTypes.ATTRACT_VIDEO) defaultMedia = mediaTypeList[1];
  if (attractLoop && attractLoop.type === MediaTypes.LINKED_CONTENT) defaultMedia = mediaTypeList[2];

  const [updateConfiguration, resetConfigAction, createMedia, resetAction, getMedia, deleteMedia] = useActions([
    configActions.updateConfiguration,
    configActions.resetAction,
    mediaActions.createMedia,
    mediaActions.resetAction,
    mediaActions.getMedia,
    mediaActions.deleteMedia
  ]);
  const clientId = useSelector(clientSelectors.selectClientId);
  const lowerDeckImages = useSelector(mediaSelectors.selectLowerDeckImages) || [];
  const { pending, successful } = useSelector(mediaSelectors.selectAction);
  const { success: configSuccess, pending: configPending } = useSelector(configSelectors.selectAction);

  const [mediaType, setMediaType] = useState(defaultMedia);
  const [activeImage, setActiveImage] = useState<MediaResponse | null>(null);
  const [editImageParams, setEditImageParams] = useState<MediaResponse | null>(null);
  const [deleteParams, setDeleteParams] = useState<MediaResponse | null | boolean>(null);
  const [showImageUploadOverlay, setShowImageUploadOverlay] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const refreshImages = () => {
    setActiveImage(null);
    setPreviewUrl('');
    resetAction();
    if (mediaType && mediaType.value && mediaType.value !== 'attract-url') {
      getMedia({ type: mediaType.value, configurationId: id });
    }
  };

  React.useEffect(() => {
    refreshImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mediaType]);

  React.useEffect(() => {
    if (successful) {
      if (mediaType && mediaType.value === MediaTypes.ATTRACT_VIDEO) {
        updateConfiguration({ id, configuration });
      }
      refreshImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  React.useEffect(() => {
    if (configSuccess) {
      resetConfigAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSuccess]);

  const sortedImages = React.useMemo(() => {
    lowerDeckImages.forEach((item) => {
      if (attractLoop && attractLoop.order) {
        let priority = attractLoop.order.indexOf(item.s3Name);
        if (priority === -1) priority = attractLoop.order.length;
        item.priority = priority;
      }
    });
    return lowerDeckImages.sort((a, b) => (a.priority || 0) - (b.priority || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowerDeckImages, attractLoop && attractLoop.order]);

  React.useEffect(() => {
    setPreviewUrl('');
    setActiveImage(null);
    if (sortedImages && sortedImages.length && !pending) {
      setActiveImage(sortedImages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedImages, pending]);

  React.useEffect(() => {
    if (activeImage) {
      const baseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/`;
      const imgObj = sortedImages.find((item: MediaResponse) => {
        return item.name === activeImage.name;
      });
      if (imgObj && imgObj.s3Name) {
        setPreviewUrl(baseUrl.concat(imgObj.s3Name));
      }
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImage]);

  const onImageUploadSuccess = (uploadResponse: MediaResponse) => {
    const newImages =
      attractLoop && attractLoop.order ? attractLoop.order.concat(uploadResponse.s3Name) : [uploadResponse.s3Name];
    updateConfiguration({
      id,
      configuration: {
        ...configuration,
        globalSettings: {
          ...globalSettings,
          attractLoop: { ...attractLoop, order: newImages }
        }
      }
    });
    setShowImageUploadOverlay(false);
  };

  const onDelete = () => {
    setPreviewUrl('');
    resetAction();
    if (mediaType && mediaType.value === MediaTypes.ATTRACT_VIDEO) {
      deleteMedia((deleteParams as MediaResponse).id);
    } else if (mediaType && mediaType.value === MediaTypes.LINKED_CONTENT) {
      if (configuration && configuration.globalSettings && configuration.globalSettings.attractLoop) {
        const newAttractLoop = { ...attractLoop };
        delete configuration.globalSettings.attractLoop.linkedContentUrl;
        updateConfiguration({
          id,
          configuration: {
            ...configuration,
            globalSettings: {
              ...globalSettings,
              attractLoop: newAttractLoop
            }
          }
        });
      }
    } else {
      deleteMedia((deleteParams as MediaResponse).id);
      const oldUrl = (deleteParams as MediaResponse).s3Name;
      const newImages =
        sortedImages && sortedImages.filter((imageUrl) => imageUrl.s3Name !== oldUrl).map((item) => item.s3Name);
      updateConfiguration({
        id,
        configuration: {
          ...configuration,
          globalSettings: {
            ...globalSettings,
            attractLoop: { ...attractLoop, order: newImages }
          }
        }
      });
    }
    setDeleteParams(null);
  };

  const onEditImageSuccess = (editResponse: MediaResponse) => {
    const oldUrl = (editImageParams as MediaResponse).s3Name;
    const newImages =
      sortedImages &&
      sortedImages.map((imageUrl) => (imageUrl.s3Name === oldUrl ? editResponse.s3Name : imageUrl.s3Name));
    updateConfiguration({
      id,
      configuration: {
        ...configuration,
        globalSettings: {
          ...globalSettings,
          attractLoop: { ...attractLoop, order: newImages }
        }
      }
    });
    setEditImageParams(null);
  };

  // RESET ACTION ON CLOSE IMAGE UPLOAD OVERLAY AND IMAGE EDIT DRAWER
  const onCancelImageUploadOverlay = () => {
    setShowImageUploadOverlay(false);
    resetAction();
  };
  const onCloseImageEditDrawer = () => {
    setEditImageParams(null);
    resetAction();
  };

  const onVideoChangeCallback = () => {
    updateConfiguration({ id, configuration });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateConfig = (payload: any) => {
    updateConfiguration({
      id,
      configuration: {
        ...configuration,
        globalSettings: {
          ...globalSettings,
          attractLoop: { ...attractLoop, ...payload }
        }
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uploadVideo = (payload: any) => {
    createMedia({ ...payload, configurationId: id });
  };

  const getDeleteDescription = () => {
    let deleteItem = '';
    if (mediaType.value === MediaTypes.ATTRACT_VIDEO) {
      deleteItem = 'video';
    } else if (mediaType.value === MediaTypes.LINKED_CONTENT) {
      deleteItem = 'link';
    } else {
      deleteItem = 'image';
    }

    return `Are you sure you want to delete this ${deleteItem}?`;
  };

  return (
    <>
      {deleteParams && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography size={40} weight={600} align="center" style={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography size={24} align="center">
                {getDeleteDescription()}
              </Typography>
            </Box>
          }
          confirmText="Yes"
          appearance="error"
          onConfirm={onDelete}
          cancelText="Cancel"
          onCancel={() => setDeleteParams(null)}
          height={300}
        />
      )}
      {showImageUploadOverlay && (
        <ImageUploadOverlay
          halfPreview
          onCancel={() => onCancelImageUploadOverlay()}
          onSuccess={onImageUploadSuccess}
          imageType={MediaTypes.ATTRACT_IMAGE}
          configurationId={id}
        />
      )}
      {(pending || configPending) && (
        <Overlay>
          <Loader color={color.BLACK} />
        </Overlay>
      )}
      <ImageEditDrawer
        halfPreview
        isOpen={!!editImageParams}
        image={editImageParams as MediaResponse}
        imageType={MediaTypes.ATTRACT_IMAGE}
        configurationId={id}
        onSuccess={onEditImageSuccess}
        onClose={() => onCloseImageEditDrawer()}
      />

      <Flex direction="column" noWrap>
        <Flex align="center" margin="0 0 6" direction="row">
          <Typography size={24} weight={700} style={{ marginRight: '20px' }}>
            Type of Media
          </Typography>
          <SelectDropdown
            items={getSortedList(mediaTypeList, 'name')}
            itemToString={(item: MediaType) => item.name}
            selectedItem={mediaType}
            onChange={(selectedMedia) => setMediaType(selectedMedia)}
          />
        </Flex>
        {mediaType.value === MediaTypes.ATTRACT_IMAGE && (
          <SlideshowImageList
            id={id}
            parentObj={attractLoop}
            configuration={props.configuration}
            slideShowImages={sortedImages as MediaResponse[]}
            previewUrl={previewUrl}
            activeImage={activeImage}
            setActiveImage={setActiveImage}
            onImageEditClick={(s3Name: string, name: string, imageId: string) =>
              setEditImageParams({ s3Name, name, id: imageId })
            }
            onAddImageClick={() => setShowImageUploadOverlay(true)}
            onDelete={(param: MediaResponse) => setDeleteParams(param)}
            updateConfig={updateConfig}
            isActive={attractLoop ? attractLoop.type === MediaTypes.ATTRACT_IMAGE : false}
            mediaType={MediaTypes.ATTRACT_IMAGE}
          />
        )}
        {mediaType.value === MediaTypes.ATTRACT_VIDEO && (
          <VideoMediaList
            activeImage={activeImage}
            setActiveImage={setActiveImage}
            onSuccess={onVideoChangeCallback}
            onDelete={(param: MediaResponse) => setDeleteParams(param)}
            isActive={attractLoop ? attractLoop.type === MediaTypes.ATTRACT_VIDEO : false}
            updateConfig={updateConfig}
            uploadVideo={uploadVideo}
            mediaType={MediaTypes.ATTRACT_VIDEO}
          />
        )}
        {mediaType.value === MediaTypes.LINKED_CONTENT && (
          <LinkedContent
            configuration={props.configuration}
            attractLoop={attractLoop}
            isActive={attractLoop ? attractLoop.type === MediaTypes.LINKED_CONTENT : false}
            updateConfig={updateConfig}
            onDelete={() => setDeleteParams(true)}
          />
        )}
      </Flex>
    </>
  );
};
